import proj4 from "proj4"
import { Coordinate, Polygon } from "@nova/protobuf"
import concaveman from "concaveman"
import type { LineWidth } from "@nova/constants"
import { DistanceUnit } from "@nova/constants"
import { default as KDBush } from "kdbush"
import * as geokdbush from "geokdbush"
import { BBox, getBVH, XY } from "@nova/bounding-volume-hierarchy"

export * from "./coordinate-conversion"

export type Latitude = number
export type Longitude = number
export type DistanceMeters = number
export type AreaMeters = number
export type Degrees = number
export type Radians = number
export type Vec2 = [number, number]
export type Vec3 = [number, number, number]

export type ProjectedBuffer = {
    coordinates: [number, number][]
    color: number
    width: LineWidth
    distance: number
    unit: DistanceUnit
}
export type CalculatedBuffer = {
    coordinates: Coordinate[]
    color: number
    width: LineWidth
    distance: number
    unit: DistanceUnit
}

type BoundingBox = {
    bottomRight: [number, number]
    topRight: [number, number]
    topLeft: [number, number]
    bottomLeft: [number, number]
    topMiddle: [number, number]
    leftMiddle: [number, number]
    rightMiddle: [number, number]
    bottomMiddle: [number, number]
    polygon: [[number, number], [number, number], [number, number], [number, number]]
    area: number
}

export type GeospatialPoint = {
    latitude: Latitude
    longitude: Longitude
}

export type ImagePoint = {
    x: number
    y: number
}

export const RadiusOfEarth = 6371 * 1000 // radius of earth in meters
const DEG = Math.PI / 180

export const KmPerDegreeLatitude = (RadiusOfEarth * DEG) / 1000

const MinCosLatitude = 0.05

export const degreeSpanForKm = (km: number, atLatitude: number) => ({
    latitude: km / KmPerDegreeLatitude,
    longitude: km / (KmPerDegreeLatitude * Math.max(MinCosLatitude, Math.cos(atLatitude * DEG))),
})

function dot2(a: Vec2, b: Vec2) {
    return a[0] * b[0] + a[1] * b[1]
}

function rad2deg(r: Radians): Degrees {
    return (r * 180) / Math.PI
}

function deg2rad(d: Degrees): Radians {
    return (d * Math.PI) / 180
}

function addMeterOffsetToCoordinate(meters: number, lat: number, lng: number) {
    const latToMeters = meters / RadiusOfEarth // Approximation for latitude
    const lngToMeters = meters / (RadiusOfEarth * Math.cos((Math.PI * lat) / 180)) // Approximation for longitude

    const degreesLat = lat + latToMeters * (180 / Math.PI)
    const degreesLng = lng + lngToMeters * (180 / Math.PI)

    return { degreesLat, degreesLng }
}

export const calculateCoordFromPointWithDistance = function (
    coord: [number, number],
    bearing: number,
    distance: number
) {
    distance = distance / RadiusOfEarth
    bearing = deg2rad(bearing)
    const lat1 = deg2rad(coord[1]),
        lng1 = deg2rad(coord[0])
    const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(distance) +
            Math.cos(lat1) * Math.sin(distance) * Math.cos(bearing)
    )

    let lng2 =
        lng1 +
        Math.atan2(
            Math.sin(bearing) * Math.sin(distance) * Math.cos(lat1),
            Math.cos(distance) - Math.sin(lat1) * Math.sin(lat2)
        )
    lng2 = ((lng2 + 3 * Math.PI) % (2 * Math.PI)) - Math.PI

    return [rad2deg(lng2), rad2deg(lat2)]
}

export function calculateCirclePolygon(
    center: { lat: number; lng: number },
    radiusMeters: number,
    segments = 64
) {
    const points: [number, number][] = []
    for (let i = 0; i <= segments; i++) {
        const bearing = (360 / segments) * i
        points.push(
            calculateCoordFromPointWithDistance(
                [center.lng, center.lat],
                bearing,
                radiusMeters
            ) as [number, number]
        )
    }
    return points
}

function norm(a: number[]) {
    let length = 0
    for (let i = 0; i < a.length; i++) {
        length += a[i] * a[i]
    }
    length = Math.sqrt(length)
    for (let i = 0; i < a.length; i++) {
        a[i] /= length
    }
    return a
}

function length(a: number[]) {
    let length = 0
    for (let i = 0; i < a.length; i++) {
        length += a[i] * a[i]
    }
    return Math.sqrt(length)
}

/**
 * Calculates the distance between two points that are very near to each other
 *
 * This does NOT do great circle distance, and so is only accurate at very small points (less than a kilometer or os)
 * @param a one point
 * @param b another point
 * @returns {number} the distance, in meters, between those two points
 */
export function calculateTrigonmetricDistance(
    a: GeospatialPoint,
    b: GeospatialPoint
): DistanceMeters {
    // just in case you forgot - latitude & longitude are degrees, this converts them to radians
    const lat1 = deg2rad(a.latitude),
        lat2 = deg2rad(b.latitude),
        lon1 = deg2rad(a.longitude),
        lon2 = deg2rad(b.longitude),
        dlat = lat2 - lat1,
        dlon = lon2 - lon1

    const t1 =
        Math.pow(Math.sin(dlat / 2), 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(dlon / 2), 2)
    // pythagorean theorem
    return 2 * RadiusOfEarth * Math.asin(Math.min(1, Math.sqrt(t1)))
}

export function calculateBoundingBox(perimeter: GeospatialPoint[]): {
    area: AreaMeters
    width: DistanceMeters
    height: DistanceMeters
    bbox: {
        minlat: number
        minlng: number
        maxlat: number
        maxlng: number
    }
} {
    let minlat = Infinity
    let minlng = Infinity
    let maxlat = -Infinity
    let maxlng = -Infinity
    for (const p of perimeter) {
        if (p.latitude < minlat) minlat = p.latitude
        if (p.latitude > maxlat) maxlat = p.latitude
        if (p.longitude < minlng) minlng = p.longitude
        if (p.longitude > maxlng) maxlng = p.longitude
    }
    // this produces 'good' results for squares away from the antimeridian, which runs through fiji & a bit of russia
    const diagonal = calculateTrigonmetricDistance(
        {
            latitude: minlat,
            longitude: minlng,
        },
        {
            latitude: maxlat,
            longitude: maxlng,
        }
    )
    const area = (diagonal * diagonal) / 2
    const a = Math.sqrt(area)
    return {
        area,
        width: a,
        height: a,
        bbox: {
            minlat,
            minlng,
            maxlat,
            maxlng,
        },
    }
}

//uses the calculateBoundingBox function to find the bounding box and formats the results as Coordinate pairs for the boxes corners
//for naming the corner points, this function assumes the map is oriented North
export function calculateBoundingBoxCornerPoints(points: GeospatialPoint[]): BoundingBox {
    const { bbox, area } = calculateBoundingBox(points)
    const { minlat, minlng, maxlat, maxlng } = bbox

    //we don't want to draw the box directly on top of the hotspots
    const { degreesLat: minLatConverted, degreesLng: minLngConverted } = addMeterOffsetToCoordinate(
        0,
        minlat,
        minlng
    )
    const { degreesLat: maxLatConverted, degreesLng: maxLngConverted } = addMeterOffsetToCoordinate(
        0,
        maxlat,
        maxlng
    )

    const bottomRight = [maxLngConverted, minLatConverted] as [number, number]
    const topRight = [maxLngConverted, maxLatConverted] as [number, number]
    const topLeft = [minLngConverted, maxLatConverted] as [number, number]
    const bottomLeft = [minLngConverted, minLatConverted] as [number, number]

    const topMiddle = [(topLeft[0] + topRight[0]) / 2, (topLeft[1] + topRight[1]) / 2] as [
        number,
        number,
    ]
    const leftMiddle = [(topLeft[0] + bottomLeft[0]) / 2, (topLeft[1] + bottomLeft[1]) / 2] as [
        number,
        number,
    ]
    const rightMiddle = [
        (topRight[0] + bottomRight[0]) / 2,
        (topRight[1] + bottomRight[1]) / 2,
    ] as [number, number]
    const bottomMiddle = [
        (bottomLeft[0] + bottomRight[0]) / 2,
        (bottomLeft[1] + bottomRight[1]) / 2,
    ] as [number, number]
    return {
        bottomRight,
        topRight,
        topLeft,
        bottomLeft,
        topMiddle,
        leftMiddle,
        rightMiddle,
        bottomMiddle,
        polygon: [topLeft, topRight, bottomRight, bottomLeft],
        area,
    }
}

export const combineBoundingBoxes = (boundingBoxes: BoundingBox[]) => {
    const points = boundingBoxes.flatMap((box) =>
        box.polygon.map((poly) => ({ longitude: poly[0], latitude: poly[1] }))
    )
    return calculateBoundingBoxCornerPoints(points)
}

export function calculateDistancePointLineSegment(
    point: GeospatialPoint,
    p1: GeospatialPoint,
    p2: GeospatialPoint
): {
    distance: DistanceMeters
    nearest: GeospatialPoint
} {
    const a = [p1.latitude, p1.longitude]
    const n = [p2.latitude - p1.latitude, p2.longitude - p1.longitude] as Vec2
    const nV = length(n)
    norm(n)
    // the line is formulated as L = a + tn; 0 < t < 1 means that it is within the line segment
    const p = [point.latitude, point.longitude]

    const intermediary = n[0] * (p[0] - a[0]) + n[1] * (p[1] - a[1])
    let nearest = {
        latitude: a[0] + intermediary * n[0],
        longitude: a[1] + intermediary * n[1],
    }

    const t = n[0] === 0 ? (nearest.longitude - a[1]) / n[1] : (nearest.latitude - a[0]) / n[0]

    // clamp to the line segment endcaps
    if (t < 0) {
        nearest = p1
    } else if (t > nV) {
        nearest = p2
    }

    const distance = calculateTrigonmetricDistance(point, nearest)

    return {
        distance,
        nearest,
    }
}

/**
 * Calculates the distance and nearest point between an arbitrary point and a polyline
 * @param point the point we want to find the closest distance to
 * @param poly the polygon - note that the code assumes that there are no donuts & that the line is not knotted at the end
 * @returns the distance & the nearest point on the polygon to the point
 */
export function calculateDistancePointPoly(
    point: GeospatialPoint,
    poly: GeospatialPoint[]
): {
    distance: DistanceMeters
    nearest: GeospatialPoint
} {
    if (poly.length < 2) {
        // cannot do point tests if fewer than two elements
        throw new Error(
            "@nova/calculate-geospatial -- calculateDistancePointPoly requires at least two points"
        )
    } else if (poly.length === 2) {
        // if exactly two, just do a line segment
        return calculateDistancePointLineSegment(point, poly[0], poly[1])
    }
    let minDistance = Infinity
    let minNearest = poly[0]
    for (let i = 0; i < poly.length; i++) {
        const { nearest, distance } = calculateDistancePointLineSegment(
            point,
            poly[i],
            poly[(i + 1) % poly.length]
        )
        if (distance < minDistance) {
            minDistance = distance
            minNearest = nearest
        }
    }
    return {
        nearest: minNearest,
        distance: minDistance,
    }
}

/**
 *
 * @param points
 */
export function calculateUpArrow(box: (GeospatialPoint & ImagePoint)[]): Vec2 {
    const maxY = Math.max(...box.map((p) => p.y)),
        minY = Math.min(...box.map((p) => p.y)),
        minX = Math.min(...box.map((p) => p.x)),
        topleft = box.filter((b) => b.x === minX && b.y === minY)[0],
        bottomleft = box.filter((b) => b.x === minX && b.y === maxY)[0]
    // okay, given all that, we now calculate the vec2
    // note that the latitude is inverted, since latitude goes from 90 (north) to -90 (south)
    // we go from 1 (north) to -1 (south) so subtraction it is
    const v = [
        topleft.latitude - bottomleft.latitude,
        topleft.longitude - bottomleft.longitude,
    ] as Vec2
    norm(v)
    return v
}

/**
 * given two reference points that run from left to right horizontally in an image, calculate how many degrees from north we are
 * @param upArrow is a vec2 pointing in the up direction of a box
 */
export function calculateBearing(upArrow: Vec2): Degrees {
    const nv = [1, 0] as Vec2
    // dot product
    const dotNvUv = dot2(nv, upArrow)
    // angle - this is radians from north in a clockwise direction
    let theta = Math.acos(dotNvUv)
    // acos goes from Math.PI to 0 as we rotate clockwise from south to north
    // this conveniently means that we just check if we have a negative x axis (are heading west-ish and remap it
    if (upArrow[1] < 0) {
        theta = 2 * Math.PI - theta
    }
    return rad2deg(theta)
}

export function calculateArea(poly: GeospatialPoint[]): AreaMeters {
    if (poly.length < 3) {
        throw new Error(
            "@nova/calculate-geospatial -- calculateArea requires at least three points"
        )
    }
    // sum the slices of the polygon, using the first point in the poly as one corner of the triangle
    let area = 0
    for (let i = 1; i < poly.length - 1; i++) {
        const origin = poly[0],
            p1 = poly[i + 0],
            p2 = poly[i + 1],
            // calculate distances
            b = norm([p1.latitude - origin.latitude, p1.longitude - origin.longitude]),
            c = norm([p2.latitude - origin.latitude, p2.longitude - origin.longitude])
        // calculate sides of the triangle
        const dab = calculateTrigonmetricDistance(origin, p1),
            dac = calculateTrigonmetricDistance(origin, p2),
            dbc = calculateTrigonmetricDistance(p1, p2)
        // calculate if it is left or right handed
        // if it is _to the right_ of the starting point, we add
        // if it is _to the left_ of the starting point, we subtract
        // this resolves concave shapes nicely, although it does mean we may have a negative total area
        // this is resolved by taking the absolute at the end
        const sign = b[0] * c[1] - b[1] * c[0] > 0 ? 1 : -1
        // angle gives us a sign on heron's formula
        const s = (dab + dac + dbc) / 2
        area += sign * Math.sqrt(Math.abs(s * (s - dab) * (s - dac) * (s - dbc)))
    }
    return Math.abs(area)
}

export function calculateIntersectionArea(
    box: {
        topLeft: [number, number]
        bottomRight: [number, number]
        topRight: [number, number]
        bottomLeft: [number, number]
    },
    area: [number, number]
): number {
    const width = area[0]
    const height = area[1]

    // Find minimum and maximum x and y coordinates
    const minX = Math.max(
        Math.min(box.topLeft[0], box.topRight[0], box.bottomLeft[0], box.bottomRight[0]),
        0
    )
    const minY = Math.max(
        Math.min(box.topLeft[1], box.topRight[1], box.bottomLeft[1], box.bottomRight[1]),
        0
    )
    const maxX = Math.min(
        Math.max(box.topLeft[0], box.topRight[0], box.bottomLeft[0], box.bottomRight[0]),
        width
    )
    const maxY = Math.min(
        Math.max(box.topLeft[1], box.topRight[1], box.bottomLeft[1], box.bottomRight[1]),
        height
    )

    // Calculate intersected width and height
    const intersectedWidth = Math.max(0, maxX - minX)
    const intersectedHeight = Math.max(0, maxY - minY)

    return intersectedWidth * intersectedHeight
}

export const projectToCRS = (
    coord: [number | undefined, number | undefined],
    projection?: string
) => {
    if (!projection) return coord as [number, number]
    return proj4("WGS84", projection, [coord[0] || 0, coord[1] || 0])
}

export const projectCoordinateToXYMeters = (coord: [number, number]) => {
    return proj4("EPSG:4326", "EPSG:3857", coord)
}

export const projectXYMetersToCoordinate = (coord: [number, number]) => {
    return proj4("EPSG:3857", "EPSG:4326", coord)
}

export function totalDistance(coordinates: Coordinate[], isPolygon = false): number {
    if (isPolygon) coordinates = ensurePolygonIsClosed(coordinates)

    let total = 0
    for (let i = 0; i < coordinates.length - 1; i++) {
        const dist = calculateTrigonmetricDistance(coordinates[i], coordinates[i + 1])
        total += dist
    }
    return total
}

function getIntersection(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2): Vec2 | null {
    const s1_x = p1[0] - p0[0]
    const s1_y = p1[1] - p0[1]
    const s2_x = p3[0] - p2[0]
    const s2_y = p3[1] - p2[1]

    const s = (-s1_y * (p0[0] - p2[0]) + s1_x * (p0[1] - p2[1])) / (-s2_x * s1_y + s1_x * s2_y)
    const t = (s2_x * (p0[1] - p2[1]) - s2_y * (p0[0] - p2[0])) / (-s2_x * s1_y + s1_x * s2_y)

    if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
        return [p0[0] + t * s1_x, p0[1] + t * s1_y]
    }

    return null // No intersection
}

export function getLineSegmentBoxIntersections(
    lineStart: GeospatialPoint,
    lineEnd: GeospatialPoint,
    box: { minx: number; miny: number; maxx: number; maxy: number }
): Vec2[] {
    const intersections: Vec2[] = []
    if (
        lineStart.longitude > box.minx &&
        lineStart.longitude < box.maxx &&
        lineStart.latitude < box.maxy &&
        lineStart.latitude > box.miny
    ) {
        intersections.push([lineStart.longitude, lineStart.latitude])
    }
    if (
        lineEnd.longitude > box.minx &&
        lineEnd.longitude < box.maxx &&
        lineEnd.latitude < box.maxy &&
        lineEnd.latitude > box.miny
    ) {
        intersections.push([lineEnd.longitude, lineEnd.latitude])
    }
    const boxEdges = [
        { p0: [box.minx, box.miny] as Vec2, p1: [box.maxx, box.miny] as Vec2 }, // Bottom edge
        { p0: [box.maxx, box.miny] as Vec2, p1: [box.maxx, box.maxy] as Vec2 }, // Right edge
        { p0: [box.maxx, box.maxy] as Vec2, p1: [box.minx, box.maxy] as Vec2 }, // Top edge
        { p0: [box.minx, box.maxy] as Vec2, p1: [box.minx, box.miny] as Vec2 }, // Left edge
    ]

    for (const edge of boxEdges) {
        const intersection = getIntersection(
            [lineStart.longitude, lineStart.latitude],
            [lineEnd.longitude, lineEnd.latitude],
            edge.p0,
            edge.p1
        )
        if (intersection) {
            intersections.push(intersection)
        }
    }

    return intersections
}

export const ensurePolygonIsClosed = (coordinates: Coordinate[]): Coordinate[] => {
    const closedPolygon = [...coordinates]
    if (
        closedPolygon.at(0)?.longitude !== closedPolygon.at(-1)?.longitude ||
        closedPolygon.at(0)?.latitude !== closedPolygon.at(-1)?.latitude
    )
        closedPolygon.push(closedPolygon[0])

    return closedPolygon
}

/**
 * Lower values mean more detail/shrink-wrapping. 2 seems like a happy medium.
 */
const CONCAVITY = 2
export const createConcaveHullPolygon = (
    coords: [number, number][],
    concavity = CONCAVITY
): [number, number][] => {
    return concaveman(coords, concavity) as [number, number][]
}

export const convertCoordinateToArray = (coord: Coordinate) => [coord.longitude, coord.latitude]
export const coordToArr = (coord: Coordinate) => [coord.longitude, coord.latitude]
export const coordToArrStrict = (coord: Coordinate) =>
    [coord.longitude, coord.latitude] as [number, number]
export const convertArrayToCoordinate = (arr: number[]) => ({ longitude: arr[0], latitude: arr[1] })
export const arrToCoord = (arr: number[]) => ({ longitude: arr[0], latitude: arr[1] })

export const isValidLongitude = (longitude: number) => longitude >= -180 && longitude <= 180
export const isValidLatitude = (latitude: number) => latitude >= -90 && latitude <= 90
export const isValidLonLat = ({ longitude, latitude }: Coordinate) =>
    isValidLongitude(longitude) && isValidLatitude(latitude)

export const isValidJSON = (json: string) => {
    try {
        JSON.parse(json)
    } catch {
        return false
    }
    return true
}

export const convertBboxToExtent = (bbox: Coordinate[]) => [
    bbox[3].longitude,
    bbox[3].latitude,
    bbox[1].longitude,
    bbox[1].latitude,
]

type WithLocation = {
    latitude?: number
    longitude?: number
    id: string
}

type WithLocationDefined = {
    latitude: number
    longitude: number
    id: string
}

// written by chatgpt, expect for the parts where it hallucinated kdbush/geokdbush methods that don't exist
export function dbscan<T extends WithLocationDefined>(
    points: T[],
    eps: number,
    minPts: number
): { clusters: T[][]; noise: T[] } {
    // 1) build a map from point → index
    const idxMap = new Map<T, number>()
    points.forEach((p, i) => idxMap.set(p, i))

    const index = new KDBush(
        points,
        (e) => e.longitude,
        (e) => e.latitude
    )

    // 3) cluster assignment array: 0 = unvisited, -1 = noise, >0 = clusterId
    const clusterId = new Int32Array(points.length)
    let nextCluster = 0

    // 4) for each point, if unvisited, expand a new cluster or mark noise
    for (let i = 0; i < points.length; i++) {
        if (clusterId[i] !== 0) continue // already processed
        const p = points[i]
        if (p.latitude == null || p.longitude == null) {
            clusterId[i] = -1 // treat as noise
            continue
        }

        // find all neighbors within eps
        const neighbors = geokdbush.around<T>(index, p.longitude, p.latitude, Infinity, eps / 1000)

        if (neighbors.length < minPts) {
            clusterId[i] = -1 // noise
        } else {
            nextCluster++
            clusterId[i] = nextCluster

            // seed the queue with all neighbors (except itself)
            const queue = neighbors.filter((j) => j !== points[i])

            for (let qi = 0; qi < queue.length; qi++) {
                const j = queue[qi]
                if (clusterId[idxMap.get(j)!] === -1) {
                    // previously marked noise → now becomes border point
                    clusterId[idxMap.get(j)!] = nextCluster
                }
                if (clusterId[idxMap.get(j)!] !== 0) {
                    // already assigned to a cluster
                    continue
                }

                // assign to this cluster
                clusterId[idxMap.get(j)!] = nextCluster

                // if j is a core point, add its neighbors to the queue
                const pj = points[idxMap.get(j)!]
                if (pj.latitude != null && pj.longitude != null) {
                    const jNeighbors = geokdbush.around<T>(
                        index,
                        pj.longitude,
                        pj.latitude,
                        Infinity,
                        eps / 1000
                    )
                    if (jNeighbors.length >= minPts) {
                        for (const n of jNeighbors) {
                            if (clusterId[idxMap.get(n)!] === 0) {
                                queue.push(n)
                            }
                        }
                    }
                }
            }
        }
    }

    // 5) collect results
    const clusters: T[][] = []
    for (let c = 1; c <= nextCluster; c++) {
        clusters.push(points.filter((_, i) => clusterId[i] === c))
    }
    const noise = points.filter((_, i) => clusterId[i] === -1)

    return { clusters, noise }
}

//uses the DBSCAN algorithm to take a list of coordinates and group them into clusters.
//anything that isnt part of the largest cluster is considered an anomaly and its ID is returned
export function findLocationAnomalies<T extends WithLocation>(
    items: T[],
    epsMeters: number = 10000,
    minPts: number = 3
): string[] {
    const filtered = items.filter(
        (item) => !!item.latitude && !!item.longitude
    ) as WithLocationDefined[]
    const { clusters } = dbscan(filtered, epsMeters, minPts)

    // Find the largest cluster
    const largestCluster = clusters.reduce((a, b) => (a.length > b.length ? a : b), [])
    // Everything not in the largest cluster is an anomaly
    const normalSet = new Set(largestCluster.map((c) => c.id as string))
    return filtered.filter((item) => !normalSet.has(item.id)).map((c) => c.id)
}

//takes a list of points and sorts them by clustering them and then sorting them from northwest to southeast
//the clustering approach ensures hotspots are numbered near their neighbours but prevents drastic jumps if we took a pure left-to-right approach
//clusters themselves are also ordered northwest to southeast
export function sortByDbscanClusters<T extends WithLocationDefined>(
    points: T[],
    eps: number = 15,
    minPts: number = 1
): T[] {
    const { clusters, noise } = dbscan(points, eps, minPts)
    // Sort clusters NW → SE by NW-most point
    clusters.sort((a, b) => {
        const nwA = a.reduce((prev, curr) =>
            curr.latitude > prev.latitude ||
            (curr.latitude === prev.latitude && curr.longitude < prev.longitude)
                ? curr
                : prev
        )
        const nwB = b.reduce((prev, curr) =>
            curr.latitude > prev.latitude ||
            (curr.latitude === prev.latitude && curr.longitude < prev.longitude)
                ? curr
                : prev
        )
        return westBiasedSort(nwA, nwB)
    })

    // Sort points within each cluster NW → SE
    const sorted: T[] = []
    for (const cluster of clusters) {
        cluster.sort(westBiasedSort)
        for (const point of cluster) {
            sorted.push(point)
        }
    }

    // Optionally add noise points at the end, sorted NW → SE
    const sortedNoise = noise.sort((a, b) => b.latitude - a.latitude || a.longitude - b.longitude)
    for (const point of sortedNoise) {
        sorted.push(point)
    }

    return sorted
}

//in the above sorting algo, we want to slightly favor west points over north points, as it makes it more logical to read
function westBiasedSort(
    a: { latitude: number; longitude: number },
    b: { latitude: number; longitude: number }
): number {
    const avgLat = (a.latitude + b.latitude) / 2
    const cosLat = Math.cos((avgLat * Math.PI) / 180)

    const deltaLatKm = (b.latitude - a.latitude) * 111 // more north wins
    const deltaLonKm = (a.longitude - b.longitude) * 111 * cosLat // more west wins

    // Very small westward bonus only if nearly tied in latitude
    const epsilon = 0.003 // ~10 meters of latitude difference
    if (Math.abs(deltaLatKm) < epsilon) {
        return deltaLonKm
    }

    return deltaLatKm
}

export function projectCoordinateToXY(point: Coordinate, center: Coordinate) {
    const centerLat = center.latitude * DEG
    const centerLng = center.longitude * DEG
    const pointLat = point.latitude * DEG
    const pointLng = point.longitude * DEG
    const deltaLng = pointLng - centerLng

    const cosineCentralAngle =
        Math.sin(centerLat) * Math.sin(pointLat) +
        Math.cos(centerLat) * Math.cos(pointLat) * Math.cos(deltaLng)

    const centralAngle = Math.acos(Math.min(1, Math.max(-1, cosineCentralAngle)))
    const scale = centralAngle === 0 ? 1 : centralAngle / Math.sin(centralAngle)

    return {
        x: RadiusOfEarth * scale * Math.cos(pointLat) * Math.sin(deltaLng),
        y:
            RadiusOfEarth *
            scale *
            (Math.cos(centerLat) * Math.sin(pointLat) -
                Math.sin(centerLat) * Math.cos(pointLat) * Math.cos(deltaLng)),
    }
}

export function pointInPolygon(point: Coordinate, polygon: Coordinate[]): boolean {
    const bvh = getBVH(polygon)
    if (!bvh.center) throw new Error("BVH missing projection center")
    const p = projectCoordinateToXY(point, bvh.center)
    let count = 0
    const stack = [bvh]

    while (stack.length) {
        const node = stack.pop()!
        if (p.y < node.bbox.minY || p.y > node.bbox.maxY || p.x > node.bbox.maxX) continue

        if (node.segments) {
            for (const s of node.segments) {
                const { x: x1, y: y1 } = s.a
                const { x: x2, y: y2 } = s.b
                if (y1 > p.y !== y2 > p.y) {
                    const xi = x1 + ((p.y - y1) * (x2 - x1)) / (y2 - y1)
                    if (xi > p.x) count++
                }
            }
        } else {
            if (node.left) stack.push(node.left)
            if (node.right) stack.push(node.right)
        }
    }

    return count % 2 === 1
}

export function pointAABBDist2(p: XY, box: BBox): number {
    let dx = 0,
        dy = 0
    if (p.x < box.minX) dx = box.minX - p.x
    else if (p.x > box.maxX) dx = p.x - box.maxX
    if (p.y < box.minY) dy = box.minY - p.y
    else if (p.y > box.maxY) dy = p.y - box.maxY
    return Math.pow(dx, 2) + Math.pow(dy, 2)
}

export function pointSegmentDist2(p: XY, a: XY, b: XY): number {
    const vx = b.x - a.x,
        vy = b.y - a.y
    const wx = p.x - a.x,
        wy = p.y - a.y
    const d2 = vx * vx + vy * vy
    const c = d2 === 0 ? 0 : (wx * vx + wy * vy) / d2
    if (c <= 0) return Math.pow(p.x - a.x, 2) + Math.pow(p.y - a.y, 2)
    if (c >= 1) return Math.pow(p.x - b.x, 2) + Math.pow(p.y - b.y, 2)
    const projX = a.x + c * vx
    const projY = a.y + c * vy
    return Math.pow(p.x - projX, 2) + Math.pow(p.y - projY, 2)
}

export function minDistToPolygon(point: Coordinate, polygon: Coordinate[]): number {
    const bvh = getBVH(polygon)
    if (!bvh.center) throw new Error("BVH missing projection center, BVH must be a root node")
    const p = projectCoordinateToXY(point, bvh.center)
    let best2 = Infinity
    const stack = [bvh]

    while (stack.length) {
        const node = stack.pop()!
        const d2Box = pointAABBDist2(p, node.bbox)
        if (d2Box >= best2) continue

        if (node.segments) {
            for (const s of node.segments) {
                const d2 = pointSegmentDist2(p, s.a, s.b)
                if (d2 < best2) best2 = d2
            }
        } else {
            const L = node.left,
                R = node.right
            const d2L = L ? pointAABBDist2(p, L.bbox) : Infinity
            const d2R = R ? pointAABBDist2(p, R.bbox) : Infinity

            if (d2L < d2R) {
                if (d2R < best2) stack.push(R!)
                if (d2L < best2) stack.push(L!)
            } else {
                if (d2L < best2) stack.push(L!)
                if (d2R < best2) stack.push(R!)
            }
        }
    }

    return Math.sqrt(best2)
}

/**
 * Omits points in polygons that are effectively the same (to 6 decimal places).
 *
 * Omits polygons that have less than 3 points.
 */
export function omitLikePoints<
    T extends [number, number, number] | [number, number] = [number, number, number],
>(polygons: T[][]): T[][] {
    const filteredPolygons = polygons.filter((polygon) => polygon.length >= 3)

    return filteredPolygons
        .map((polygon) => {
            const pointsVisited = new Set<string>()
            const uniquePoints = [] as typeof polygon

            for (const point of polygon) {
                const roundedPoint = point.map((coord) => Number(coord.toFixed(6)))
                const key = roundedPoint.join(",")

                if (!pointsVisited.has(key)) {
                    pointsVisited.add(key)
                    uniquePoints.push(point)
                }
            }

            return uniquePoints
        })
        .filter((polygon) => polygon.length >= 3) as T[][]
}

export const arePolygonsEqual = (a: Polygon[], b: Polygon[]) => {
    if (a.length !== b.length) return false
    return a.every((p1, i) => {
        const p2 = b[i]
        if (p1.coordinates.length !== p2.coordinates.length) return false
        return p1.coordinates.every((coord1, i2) => {
            const coord2 = p2.coordinates[i2]
            return (
                Math.abs(coord1.longitude - coord2.longitude) < 1e-7 &&
                Math.abs(coord1.latitude - coord2.latitude) < 1e-7
            )
        })
    })
}

export const isNullIsland = ({ lat, lng }: { lat: number; lng: number }) => {
    return Math.abs(lat) < 1 && Math.abs(lng) < 1
}

export * from "./region"
export * from "./bounds"
export * from "./polygon-area"
export * from "./polygon-simplify"
export * from "./subtract-polygon"
export * from "./to-geojson"
